import { useEffect, useState } from "react";
import {
    PersonDeleteRegular
} from "@fluentui/react-icons";
import { Label, makeStyles, shorthands } from "@fluentui/react-components";
import { IRemoveGuestUsersActionRequest, IRemoveGuestUsersSelection } from "@interfaces/actions/guest-user-actions.interfaces";
import { IRemoveGuestUsersActionFormProps } from "./remove-guest-users-action-form.props";
import WorkspaceCard from "components/common/workspace-card/workspace-card";
import PrincipalCard from "components/common/principal-card/principal-card";
import ActionFormButtons from "components/actions-common/action-buttons/action-form-buttons";
import ActionFormRequestMessage from "components/actions-common/action-form-request-message/action-form-request-message";
import { ILocaleStrings } from "loc/locale.interfaces";
import { useL10n } from "context/l10n-context";

const layoutStyles = makeStyles({
    mainWrapper: {
        display: 'flex',
        flexDirection: 'column'
    },
    itemWrapper: {
        ...shorthands.margin('1em', 0, 0, 0)
    }
});


export default function RemoveGuestUsersActionForm(props: IRemoveGuestUsersActionFormProps) {
    const styles = layoutStyles();
    const { t } = useL10n<ILocaleStrings>();

    const [isValid, setIsValid] = useState<boolean>(false);

    const [selection, setSelection] = useState<IRemoveGuestUsersSelection>();

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [canRemoveWorkspace, setCanRemoveWorkspace] = useState<boolean>(true);
    const [canRemovePrincipal, setCanRemovePrincipal] = useState<boolean>(true);

    const [requestMessage, setRequestMessage] = useState<string>(``);

    const onSubmitHandler = async (): Promise<any> => {
        if (!props.onSubmit) {
            return Promise.reject();
        }

        const requestModel: IRemoveGuestUsersActionRequest = {};

        if (selection?.site)
            requestModel.workspace = { siteUrl: selection.site.url };
        if (selection?.user)
            requestModel.user = { upn: selection.user.upn };

        setIsSubmitting(true);

        return props.onSubmit("/api/actions/remove-guest-users", requestModel).then(
            (data: any) => {
                console.log(`RemoveGuestUsersActionForm -> onSubmitHandler -> success ->`, data);
                setIsSubmitting(false);
                return data;
            },
            (error: any) => {
                console.log(`RemoveGuestUsersActionForm -> onSubmitHandler -> error ->`, error);
                setIsSubmitting(false);
                return Promise.reject(error);
            });
    }

    useEffect(() => {
        const selection: IRemoveGuestUsersSelection = {};
        if (props.model) {
            if (props.model.site) {
                selection.site = {
                    url: props.model.site.url,
                    name: props.model.site.name,
                    workspaceType: props.model.site.workspaceType
                };
            }
            if (props.model.user) {
                selection.user = {
                    upn: props.model.user.upn,
                    name: props.model.user.name
                };
            }
        }
        setSelection(selection);
    }, [props.model]);

    useEffect(() => {
        if (isSubmitting) {
            setCanRemoveWorkspace(false);
            setCanRemovePrincipal(false);
        }
        else {
            setCanRemovePrincipal(!selection?.site === false);
            setCanRemoveWorkspace(!selection?.user === false);

            // Set the request message based on the selection.
            var requestMessageKey: keyof ILocaleStrings = "unknown";

            const params = {
                userDisplayName: selection?.user?.name,
                siteTitle: selection?.site?.name
            };

            if (selection?.site && selection?.user) {
                requestMessageKey = "ppaRemoveGuestUsersActionRequestMessageUserAndSite";
            }
            else if (selection?.user) {
                requestMessageKey = "ppaRemoveGuestUsersActionRequestMessageUserOnly";
            }
            else if (selection?.site) {
                requestMessageKey = "ppaRemoveGuestUsersActionRequestMessageSiteOnly";
            }

            setRequestMessage(t(requestMessageKey, params));
        }
    }, [isSubmitting, selection]);

    const removePrincipal = (): void => {
        setSelection({
            ...selection,
            user: undefined
        });
    }

    const removeWorkspace = (): void => {
        setSelection({
            ...selection,
            site: undefined
        });
    }

    return (
        <div className={styles.mainWrapper}>
            {selection?.user &&
                <div className={styles.itemWrapper}>
                    <Label>{t('ppaGuestUser')}</Label>
                    <PrincipalCard
                        item={{ upn: selection.user.upn, displayName: selection.user.name }}
                        onDismissClick={canRemovePrincipal ? removePrincipal : undefined} />
                </div>}
            {selection?.site &&
                <div className={styles.itemWrapper}>
                    <Label>{t('ppaWorkspace')}</Label>
                    <WorkspaceCard
                        item={{ title: selection.site.name, siteUrl: selection.site.url, workspaceType: selection.site.workspaceType }}
                        onDismissClick={canRemoveWorkspace ? removeWorkspace : undefined} />
                </div>}
            <ActionFormRequestMessage
                icon={<PersonDeleteRegular />}
                message={requestMessage} />
            <ActionFormButtons
                onSubmit={onSubmitHandler}
                onCancel={props.onCancel} />
        </div>
    );
}